'use client'

/**
 * The precheck class, rendered beside the answer it governed (#14083).
 *
 * ## Why the sources are nested rather than listed
 *
 * Every citation is rendered INSIDE the claim it supports, and that nesting is
 * the requirement rather than a layout preference: a source list at the foot of
 * an answer is a bibliography, and a bibliography does not tell a
 * superintendent which sentence came from the strategic plan and which came
 * from a task record. `OrgPageAskBox` (#11635) is the precedent this follows —
 * citations always visible, never behind a disclosure, naming what they cite.
 * The data shape makes the alternative impossible to render: an
 * `AnswerClaim` OWNS its citations, so there is no flat list to put at the end.
 *
 * ## Why nothing here is markdown
 *
 * `OrgPageAskBox`'s Security Review M2: an answer that can embed untrusted
 * text reached through prompt injection must not be rendered as markdown,
 * because markdown turns an injected instruction into a clickable link inside
 * what the product presents as a grounded, cited answer. Every string in an
 * `AnswerClassPresentation` comes from a closed vocabulary today
 * (`answer-class-shapes.ts` says so and a unit test pins it), so this is the
 * cheap half of that rule — but it is the half that has to already be true on
 * the day someone routes model prose through here.
 *
 * ## What `parts` governs, and what this component governs
 *
 * `ANSWER_CLASS_SHAPES[class].parts` is the instruction set for the MODEL's
 * prose — each part carries an `instruction` string that is projected into the
 * prompt. This component renders the STRUCTURED affordances derived from the
 * plan, which is a related but separate contract: NOT_YET declares four parts
 * and no `unlock` one, yet the block still renders its unlocks, because
 * "here is what it would take" is that class's own headline and the unlock is
 * the only thing that says it. So a role present here and absent there is not
 * automatically drift.
 *
 * What WOULD be drift is the two disagreeing about ORDER for a role they both
 * carry — PARTIAL answering before it bounds, above all. That is asserted
 * against `partRoles()` rather than against this file's own layout, in
 * `answer-class-block-14083.test.tsx`; a DOM-order test that reads only this
 * component passes no matter what the table says.
 *
 * ## One control, and only when there is a record to write to
 *
 * No links and no disclosure. The one interactive element is a stand-in's
 * "Use … instead" button (#14086), rendered ONLY when the turn wrote an
 * unmet-need record: the click records acceptance on that record
 * (`acceptUnmetNeedSubstituteAction`, an update, never an insert) and asks
 * for the stand-in as the next turn. A native `<button>`, so it is reachable
 * and operable by keyboard with no focus management of its own. Without a
 * record the stand-in stays the plain sentence it was.
 *
 * One stand-in per turn: once any is taken, every stand-in on that turn is
 * disabled, so two cannot overwrite each other's `substituteAccepted` or
 * launch two follow-up turns. A failed acceptance never blocks the ask — the
 * asker still gets the stand-in they chose. The follow-up is sent marked
 * (`substituteFollowUp`), so the route does not record it as a second,
 * machine-authored unmet need.
 *
 * When a NOT_YET turn renders its preview (`./not-yet-preview.tsx`), this
 * block does NOT render its own question: the preview's multi-select is the
 * one question the turn asks, and two on screen is the five-question survey
 * the PRD forbids in a politer form.
 *
 * Semantic tokens only, no `dark:` variants, no emoji: `AGENTS.md` § Design
 * System. Icons come from `@/components/icons`, never `lucide-react`.
 */

import { useRef, useState } from 'react'

import { acceptUnmetNeedSubstituteAction } from '@/app/actions/unmet-needs'
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Compass,
  HelpCircle,
  Search,
  ShieldCheck,
} from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type {
  AnswerClaim,
  AnswerClassPresentation,
  SubstitutePresentation,
} from '@/lib/ai-chat/answer-class-shapes'
import {
  DISTRICT_DIFFERS_LABEL,
  orderTieredClaims,
} from '@/lib/ai-chat/source-tiers'
import type { DeclinedPresentation } from '@/lib/ai-chat/declined-presentation'
import type { AnswerClass } from '@/lib/capability-ledger'
import type { UnmetNeedTurn } from '@/lib/unmet-needs/not-yet-preview'

import { useAiChat } from './ai-chat-provider'
import { TierBadge } from './tier-badge'

/**
 * One glyph per class, so the six are distinguishable before the copy is
 * read. Exhaustive over the vocabulary — a seventh class is a compile error.
 *
 * `PARTIAL` takes the unfilled `Circle` rather than a second `CheckCircle2`:
 * the two classes are adjacent in meaning, which is exactly why the glyph has
 * to separate them. A completed check against a partial answer reads as "fully
 * answered" at the glance this row exists to serve, and the boundary sentence
 * that corrects it is a line further down.
 */
export const CLASS_ICONS = {
  GROUNDED: CheckCircle2,
  PARTIAL: Circle,
  ADJACENT: Compass,
  NOT_YET: HelpCircle,
  DECLINED: ShieldCheck,
  // #16824 — "where would I look?", not "missing": nothing was found absent.
  NOT_UNDERSTOOD: Search,
} as const satisfies Record<AnswerClass, typeof CheckCircle2>

/**
 * A DECLINED turn's body (#16821): why, naming the rule, then the aggregate
 * this role may see. No answer slot — the withheld answer has no stand-in on
 * screen. The next steps (a pivot and a Route draft) render below the block,
 * as on every other non-GROUNDED turn.
 */
function DeclinedSection({
  boundary,
  declined,
}: {
  boundary: string | null
  declined: DeclinedPresentation
}) {
  return (
    <div data-testid="answer-class-declined" className="mt-2 space-y-2">
      {boundary && (
        <p
          data-testid="ai-chat-answer-boundary"
          className="text-sm text-text-secondary"
        >
          {boundary}
        </p>
      )}
      <p className="text-xs text-text-secondary">
        Rule:{' '}
        <span
          data-testid="ai-chat-answer-rule"
          className="font-semibold text-text-primary"
        >
          {declined.ruleLabel}
        </span>
      </p>
      {declined.adjacent.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-text-primary">
            What you can see instead
          </p>
          <ul className="mt-1 space-y-1">
            {declined.adjacent.map(entry => (
              <li
                key={entry.intent}
                data-testid="ai-chat-answer-adjacent"
                className="text-sm text-text-secondary"
              >
                {entry.claim}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/** A source chip. Not a pill-shaped control — it does nothing when pressed. */
function CitationChip({ label }: { label: string }) {
  return (
    <span className="rounded-md bg-surface-elevated px-2 py-0.5 text-xs text-text-secondary">
      {label}
    </span>
  )
}

/**
 * One claim, with its own sources beside it rather than beneath the block,
 * and its source tier (#16825) at the end. A best-practice claim the
 * district's own guidance overrides says so before its badge.
 */
function ClaimRow({ claim }: { claim: AnswerClaim }) {
  return (
    <li
      data-testid="ai-chat-answer-claim"
      data-tier={claim.tier}
      className="flex flex-wrap items-baseline gap-x-2 gap-y-1"
    >
      <span className="text-sm text-text-primary">
        {claim.claim}
        {claim.grounding === 'partial' ? ' (in part)' : ''}
      </span>
      {claim.citations.map(citation => (
        <CitationChip key={citation.domain} label={citation.label} />
      ))}
      {claim.differsFromDistrict && (
        <span
          data-testid="ai-chat-tier-differs"
          className="text-xs text-text-secondary"
        >
          {DISTRICT_DIFFERS_LABEL}
        </span>
      )}
      <TierBadge tier={claim.tier} />
    </li>
  )
}

/** One short, specific sentence about what is missing. */
function Boundary({ text }: { text: string }) {
  return (
    <p
      data-testid="ai-chat-answer-boundary"
      className="mt-2 text-sm text-text-secondary"
    >
      {text}
    </p>
  )
}

/** What the asker reads under a NOT_UNDERSTOOD boundary (#16824). */
export const NOT_UNDERSTOOD_HINT =
  'Try one of the questions below, or send the drafted note to SchoolAI ' +
  'support.'

/**
 * A question nobody understood (#16824): the boundary, then a line that
 * points at the Next steps row under the block — the pivots and the support
 * draft are rendered there, by `NextSteps`, like every other class's steps.
 * Never "I can't help with that": the turn always ends with somewhere to go.
 */
function NotUnderstoodBody({ boundary }: { boundary: string | null }) {
  return (
    <div data-testid="answer-class-not-understood">
      {boundary && <Boundary text={boundary} />}
      <p className="mt-1 text-xs text-text-secondary">{NOT_UNDERSTOOD_HINT}</p>
    </div>
  )
}

/** The follow-up turn a taken stand-in asks for, in the asker's voice. */
function substituteFollowUp(substitute: SubstitutePresentation): string {
  return `Show me ${substitute.label} instead of ${substitute.standsInFor}.`
}

/**
 * A stand-in the asker can take (#14086). Taking one is owned by
 * {@link SubstituteChoices}, which holds the one-per-turn state; this renders
 * the row and its button.
 */
function SubstituteChoice({
  substitute,
  disabled,
  onTake,
}: {
  substitute: SubstitutePresentation
  disabled: boolean
  onTake: (substitute: SubstitutePresentation) => void
}) {
  return (
    <li
      data-testid="ai-chat-answer-substitute"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-secondary"
    >
      <span>{`Instead of ${substitute.standsInFor}: ${substitute.label}`}</span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="pointer-coarse:min-h-11"
        aria-label={`Use this instead: ${substitute.label}`}
        disabled={disabled}
        onClick={() => onTake(substitute)}
      >
        Use this instead
      </Button>
    </li>
  )
}

/**
 * The stand-ins on one turn, of which at most one can be taken.
 *
 * Records acceptance on the turn's unmet-need record, then asks for it — the
 * record says whether the substitute worked, and the next turn is the
 * substitute. The ref closes the double-click window a state update leaves
 * open between two clicks in one frame.
 */
function SubstituteChoices({
  substitutes,
  unmetNeedId,
}: {
  substitutes: readonly SubstitutePresentation[]
  unmetNeedId: string
}) {
  const { sendMessage, isStreaming } = useAiChat()
  const [taken, setTaken] = useState(false)
  const takenRef = useRef(false)

  async function take(substitute: SubstitutePresentation) {
    if (takenRef.current) return
    takenRef.current = true
    setTaken(true)
    try {
      // Awaited so the record lands before the next turn starts, but its
      // outcome — including a rejection — never gates the ask.
      await acceptUnmetNeedSubstituteAction({
        unmetNeedId,
        substitute: substitute.source,
      })
    } catch {
      // Recording is best-effort; the ask below must still go — see sub-issue R carve-out
      // (logged server-side when it got that far; nothing for the asker to do).
    }
    await sendMessage(substituteFollowUp(substitute), undefined, {
      substituteFollowUp: true,
    })
  }

  return (
    <>
      {substitutes.map(substitute => (
        <SubstituteChoice
          key={substitute.source}
          substitute={substitute}
          disabled={taken || Boolean(isStreaming)}
          onTake={choice => void take(choice)}
        />
      ))}
    </>
  )
}

/**
 * The block, or `null` for a turn that was never planned.
 *
 * `null` is every surface but Home today — the docked panel and the
 * ActionBoard drawer send no presentation at all, and their transcript is
 * byte-for-byte what it was.
 *
 * It is also `null` for every REPLAYED turn, and that is a real limitation
 * rather than an oversight: the presentation rides the `done` frame and is
 * never written to the message row, so reopening a conversation renders the
 * answer text without its class, its boundary or its citations. Persisting it
 * is a schema change this issue deliberately does not make. Until then, do not
 * read a missing block as "this turn was unplanned".
 */
export function AnswerClassBlock({
  presentation,
  unmetNeed = null,
}: {
  presentation: AnswerClassPresentation | null
  /** The record this turn wrote (#14086); `null` when it wrote none. */
  unmetNeed?: UnmetNeedTurn | null
}) {
  // #16822 — a CLARIFY turn's reply IS its question, and its choices render
  // as its next step; a block repeating "Which one do you mean?" adds nothing.
  if (presentation === null || presentation.answerClass === 'CLARIFY') {
    return null
  }
  // #16823 — a scoping question asks instead of answering, too: nothing to
  // classify, and its question renders as the scope step.
  if (presentation.nextSteps.some(step => step.kind === 'scope')) return null

  const Icon = CLASS_ICONS[presentation.answerClass]

  return (
    <Card
      data-testid="ai-chat-answer-class"
      data-answer-class={presentation.answerClass}
      className="mt-3 border-stroke-extra-light bg-surface-panel p-3 shadow-none"
    >
      <h4 className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
        <Icon
          className="h-3.5 w-3.5 shrink-0 text-text-secondary"
          aria-hidden
        />
        {presentation.headline}
      </h4>

      {presentation.assumption && (
        <p
          data-testid="ai-chat-scope-assumption"
          className="mt-2 text-sm text-text-secondary"
        >
          {presentation.assumption}
        </p>
      )}

      {presentation.claims.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {/* #16825 — re-ordered here too: a flagged best practice goes
            after the district claim it differs from, whatever order the
            frame carried. One intent can now have two claims. */}
          {orderTieredClaims(presentation.claims).map((claim, index) => (
            // Intent and tier alone can repeat; the claims are re-derived per
            // frame, so the position is stable for a given list.
            <ClaimRow
              key={`${claim.intent}:${claim.tier}:${index}`}
              claim={claim}
            />
          ))}
        </ul>
      )}

      {presentation.declined ? (
        <DeclinedSection
          boundary={presentation.boundary}
          declined={presentation.declined}
        />
      ) : presentation.answerClass === 'NOT_UNDERSTOOD' ? (
        <NotUnderstoodBody boundary={presentation.boundary} />
      ) : (
        presentation.boundary && <Boundary text={presentation.boundary} />
      )}

      {presentation.substitutes.length > 0 && (
        <ul className="mt-2 space-y-1">
          {unmetNeed ? (
            <SubstituteChoices
              substitutes={presentation.substitutes}
              unmetNeedId={unmetNeed.id}
            />
          ) : (
            presentation.substitutes.map(substitute => (
              <li
                key={substitute.source}
                data-testid="ai-chat-answer-substitute"
                className="text-sm text-text-secondary"
              >
                {`Instead of ${substitute.standsInFor}: ${substitute.label}`}
              </li>
            ))
          )}
        </ul>
      )}

      {presentation.question && !unmetNeed?.preview && (
        <p
          data-testid="ai-chat-answer-question"
          className="mt-2 text-sm text-text-primary"
        >
          {presentation.question}
        </p>
      )}

      {presentation.unlocks.length > 0 && (
        <ul className="mt-2 space-y-1">
          {presentation.unlocks.map(unlock => (
            <li
              key={unlock.domain}
              data-testid="ai-chat-answer-unlock"
              className="flex items-center gap-1.5 text-xs text-text-secondary"
            >
              <ArrowRight className="h-3 w-3 shrink-0" aria-hidden />
              {`${unlock.label} — ${unlock.owner}`}
            </li>
          ))}
        </ul>
      )}

      {presentation.routes.length > 0 && (
        <ul className="mt-2 space-y-1">
          {presentation.routes.map(route => (
            <li
              key={route.domain}
              data-testid="ai-chat-answer-route"
              className="text-xs text-text-secondary"
            >
              {`Where this lives today: ${route.label}`}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
